<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import companyIntroBuilding from '../assets/company-intro-building.jpg';
import heroBanner from '../assets/hero-body-scanners-base.webp';
import {
  getArticles,
  getBootstrap,
  getProducts,
  getVideos,
  type Article,
  type Bootstrap,
  type Contact,
  type Product,
  type Video
} from '../api';
import { isZh, locale, text } from '../i18n';

type LocalText = { zh: string; en: string };
type HomeSetting = {
  heroImage?: string;
  eyebrow?: LocalText;
  title?: LocalText;
  summary?: LocalText;
  assurances?: Array<{ title?: LocalText; detail?: LocalText }>;
};
type ProductPrioritySetting = {
  homeTopProductId?: number | null;
  topProductIds?: number[];
};

const COMPANY_IMAGE = companyIntroBuilding;

const fallbackFamilies = [
  { slug: 'x-ray-security-body-scanner', name: { zh: '微剂量X射线人体安检仪', en: 'Micro-dose X-ray Body Scanner' }, copy: { zh: '面向监所、海关、机场及重点场所的人体隐匿物成像检查', en: 'Imaging inspection for concealed objects at prisons, customs, airports and critical sites' } },
  { slug: 'mmw-people-screening', name: { zh: '毫米波人体安检仪', en: 'Millimeter-wave Body Scanner' }, copy: { zh: '非接触式人体表面违禁物筛查，兼顾通行效率与人员体验', en: 'Contactless screening with efficient passenger flow' } },
  { slug: 'detection-body-scanner', name: { zh: '太赫兹人体安检仪', en: 'Terahertz Body Scanner' }, copy: { zh: '适用于高通量入口的被动式人体异常物品探测', en: 'Passive detection for high-throughput security entrances' } },
  { slug: 'integrated-detention-informaiton-system', name: { zh: '收押一体化信息采集平台', en: 'Integrated Detention Intake Platform' }, copy: { zh: '整合人员登记、信息采集、安检流程与业务协同', en: 'Registration, information capture, screening and operational workflows' } },
  { slug: 'security-screening-system', name: { zh: '检索综合信息系统', en: 'Integrated Screening Information System' }, copy: { zh: '统一汇聚安检记录、图像资料与业务数据，支持快速检索', en: 'Screening records, images and operational data in one searchable system' } }
];

const applications = [
  { title: { zh: '看守所、监狱、执法办案中心等', en: 'Justice and corrections' }, copy: { zh: '为收押、会见及重点区域提供人体隐匿物检查与流程协同。', en: 'Concealed-object screening and coordinated workflows for detention, visitation and controlled areas.' } },
  { title: { zh: '边检、海关、民航等', en: 'Airports and customs' }, copy: { zh: '支持关键通道的快速筛查、图像复核和规范化处置。', en: 'Fast screening, image review and standardized handling at critical checkpoints.' } },
  { title: { zh: '矿山、物流、贵金属加工厂等', en: 'Mining operations' }, copy: { zh: '面向高风险生产区域，强化违禁物品与夹带行为检查。', en: 'Stronger screening for prohibited items and concealed materials in high-risk production areas.' } },
  { title: { zh: '重点要害部门', en: 'Critical facilities' }, copy: { zh: '适配政务、会议、公共交通等高安全等级入口。', en: 'Configurable screening for government, conference and public-transport entrances.' } }
];

const deliverySteps = [
  { title: { zh: '需求与现场评估', en: 'Requirements and site assessment' }, copy: { zh: '确认检查对象、通行量、现场空间与管理要求。', en: 'Define screening targets, throughput, space and operating requirements.' } },
  { title: { zh: '方案与系统配置', en: 'Solution and system configuration' }, copy: { zh: '完成设备选型、软件接口和实施计划设计。', en: 'Select equipment, define software interfaces and plan implementation.' } },
  { title: { zh: '安装与人员培训', en: 'Installation and training' }, copy: { zh: '执行部署调试、操作培训和项目验收。', en: 'Commission the deployment, train operators and complete acceptance.' } },
  { title: { zh: '持续技术支持', en: 'Ongoing technical support' }, copy: { zh: '提供运行维护、远程支持与后续升级服务。', en: 'Provide maintenance, remote support and future upgrades.' } }
];

const fallbackAssurances = [
  { title: { zh: '自主研发', en: 'In-house R&D' }, detail: { zh: '核心成像与智能识别技术', en: 'Core imaging and intelligent recognition' } },
  { title: { zh: '场景适配', en: 'Site adaptation' }, detail: { zh: '覆盖多类高安全等级入口', en: 'Configured for demanding security entrances' } },
  { title: { zh: '项目交付', en: 'Project delivery' }, detail: { zh: '方案、部署、培训与服务闭环', en: 'Planning, deployment, training and service' } }
];

const bootstrap = ref<Bootstrap | null>(null);
const products = ref<Product[]>([]);
const cases = ref<Article[]>([]);
const videos = ref<Video[]>([]);
const loading = ref(true);
const failed = ref(false);
const activeCaseIndex = ref(0);
const activeVideoIndex = ref(0);
const videoStarted = ref(false);
const videoElement = ref<HTMLVideoElement | null>(null);
let loadSequence = 0;

const home = computed(() => (bootstrap.value?.settings['site.home'] || {}) as HomeSetting);
const productPriority = computed(() => (bootstrap.value?.settings['site.product-priority'] || {}) as ProductPrioritySetting);
const localized = (value?: Partial<LocalText>) => isZh.value ? value?.zh || '' : value?.en || '';
const fallbackHero = {
  eyebrow: { zh: '专业智能人体安检系统与解决方案', en: 'Integrated security screening solutions' },
  title: { zh: '人体扫描成像技术领导者', en: 'Leader of Personnel Scanning & Imaging Technology' },
  summary: {
    zh: '聚焦微剂量X射线、毫米波与太赫兹人体安检技术，为司法监所、机场海关、矿山和重点场所提供设备、软件及项目交付服务。',
    en: 'Micro-dose X-ray, millimeter-wave and terahertz screening equipment, software and project delivery for corrections, airports, customs, mining and critical facilities.'
  }
};
const heroEyebrow = computed(() => localized(home.value.eyebrow) || localized(fallbackHero.eyebrow));
const heroTitle = computed(() => {
  const title = localized(home.value.title) || localized(fallbackHero.title);
  return title.replace(/\s*\r?\n\s*/g, isZh.value ? '' : ' ');
});
const heroSummary = computed(() => localized(home.value.summary) || localized(fallbackHero.summary));
const assurances = computed(() => home.value.assurances?.length ? home.value.assurances : fallbackAssurances);
const familyItems = computed(() => {
  const categories = bootstrap.value?.categories || [];
  if (!categories.length) return fallbackFamilies;

  // 分类顺序和内容以公开接口为准。旧分类仍保留本地文案作为兼容兜底，
  // 新增分类则直接使用后台维护的名称和描述，避免首页继续显示固定列表。
  return categories.map((category) => {
    const fallback = fallbackFamilies.find((item) => item.slug === category.slug);
    const copy = category.description || fallback?.copy[isZh.value ? 'zh' : 'en'] || '';
    return {
      slug: category.slug,
      name: { zh: category.name, en: category.name },
      copy: { zh: copy, en: copy }
    };
  });
});
const featuredProduct = computed(() => {
  const homeTopProductId = productPriority.value.homeTopProductId;
  return (typeof homeTopProductId === 'number' ? products.value.find((item) => item.id === homeTopProductId) : undefined)
    || products.value.find((item) => item.category.slug === 'x-ray-security-body-scanner')
    || products.value[0];
});
const featuredProductImage = computed(() => featuredProduct.value?.media.find((item) => item.role === 'primary')?.url || featuredProduct.value?.media[0]?.url || '');
const activeCase = computed(() => cases.value[activeCaseIndex.value]);
const activeVideo = computed(() => videos.value[activeVideoIndex.value]);
const primaryContactChannels = computed(() => {
  const channels = bootstrap.value?.contact?.channels ?? [];
  const selected = channels.filter((channel) => channel.isPrimary);
  return (selected.length ? selected : channels).slice(0, 3);
});

function contactHref(channel: Contact['channels'][number]): string | null {
  if (channel.href) return channel.href;
  if (channel.type === 'phone') return `tel:${channel.value.replace(/[^+\d]/g, '')}`;
  if (channel.type === 'email') return `mailto:${channel.value}`;
  if ((channel.type === 'social' || channel.type === 'website') && /^https?:\/\//i.test(channel.value)) return channel.value;
  return null;
}

function isExternalContactHref(href: string | null): boolean { return Boolean(href && /^https?:\/\//i.test(href)); }

async function loadHome() {
  const sequence = ++loadSequence;
  loading.value = true;
  failed.value = false;
  try {
    const [boot, productResult, caseResult, videoResult] = await Promise.all([
      getBootstrap(locale.value),
      getProducts(locale.value, { pageSize: '12' }),
      getArticles(locale.value, 'case'),
      getVideos(locale.value, 10)
    ]);
    if (sequence !== loadSequence) return;
    bootstrap.value = boot;
    products.value = productResult.data;
    cases.value = caseResult.data.slice(0, 5);
    videos.value = videoResult.filter((item) => item.playbackUrl);
    activeCaseIndex.value = 0;
    activeVideoIndex.value = 0;
    videoStarted.value = false;
  } catch {
    if (sequence === loadSequence) failed.value = true;
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}

watch(locale, loadHome, { immediate: true });

function showCase(offset: number) {
  if (!cases.value.length) return;
  activeCaseIndex.value = (activeCaseIndex.value + offset + cases.value.length) % cases.value.length;
}

function selectVideo(index: number) {
  activeVideoIndex.value = index;
  videoStarted.value = false;
}

async function playActiveVideo() {
  videoStarted.value = true;
  await nextTick();
  await videoElement.value?.play().catch(() => {
    videoStarted.value = false;
  });
}

function formatDuration(seconds: number | null) {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

function caseCategory(item: Article) {
  if (/监狱|监所|看守所|司法/.test(item.title)) return text('司法监所', 'Justice and corrections');
  if (/机场|海关|交通/.test(item.title)) return text('机场与交通', 'Airport and transport');
  if (/矿|煤/.test(item.title)) return text('矿山企业', 'Mining');
  return text('重点场所', 'Critical facilities');
}
</script>

<template>
  <div class="legacy-pages">
    <section
      class="portal-home-hero"
      aria-labelledby="home-banner-title"
    >
      <img
        class="portal-home-hero__image"
        :src="heroBanner"
        :alt="text('技卫士人体扫描设备与人体成像效果', 'JWS body scanners and personnel imaging results')"
        width="2116"
        height="743"
        decoding="async"
        fetchpriority="high"
      >
      <div class="container portal-home-hero__inner">
        <div class="portal-home-hero__copy">
          <span class="portal-eyebrow">{{ heroEyebrow }}</span>
          <h1 id="home-banner-title">
            {{ heroTitle }}
          </h1>
          <p>{{ heroSummary }}</p>
          <div class="portal-actions">
            <RouterLink
              class="button"
              to="/products"
            >
              {{ text('了解产品', 'Explore products') }}
            </RouterLink>
            <RouterLink
              class="text-link"
              to="/contact"
            >
              {{ text('获取项目方案', 'Discuss a project') }} <b aria-hidden="true">→</b>
            </RouterLink>
          </div>
        </div>
      </div>
      <div class="portal-home-hero__assurance">
        <div class="container">
          <div
            v-for="item in assurances"
            :key="localized(item.title)"
          >
            <strong>{{ localized(item.title) }}</strong><span>{{ localized(item.detail) }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="portal-section">
      <div class="container">
        <header class="portal-section-heading portal-section-heading--products">
          <span>{{ text('主要产品', 'Core products') }}</span>
          <h2>{{ text('微剂量X射线、毫米波、太赫兹人体安检仪', 'From screening equipment to operational platforms') }}</h2>
          <p>{{ text('围绕人体安检的不同技术路线和业务流程，形成可按项目组合的产品体系。', 'A project-ready portfolio spanning screening technologies and operational workflows.') }}</p>
        </header>
        <div
          v-if="featuredProduct"
          class="portal-product-lead"
        >
          <RouterLink
            class="portal-product-lead__visual"
            :to="`/products/${featuredProduct.slug}`"
          >
            <img
              v-if="featuredProductImage"
              :src="featuredProductImage"
              :alt="featuredProduct.name"
            >
            <div
              v-else
              class="media-placeholder"
            >
              JWS
            </div>
            <span>{{ featuredProduct.name }}</span>
          </RouterLink>
          <div class="portal-product-lead__families">
            <RouterLink
              v-for="(item, index) in familyItems"
              :key="item.slug"
              class="portal-family-row"
              :to="`/products?category=${item.slug}`"
            >
              <span>{{ String(index + 1).padStart(2, '0') }}</span>
              <span><strong>{{ localized(item.name) }}</strong><small>{{ localized(item.copy) }}</small></span>
              <b aria-hidden="true">→</b>
            </RouterLink>
          </div>
        </div>
        <div
          v-else-if="loading"
          class="portal-loading"
        >
          {{ text('正在加载产品…', 'Loading products…') }}
        </div>
        <p
          v-else-if="failed"
          class="empty-state"
        >
          {{ text('产品数据暂时无法加载，请稍后重试。', 'Product data is temporarily unavailable.') }}
        </p>
      </div>
    </section>

    <section class="portal-section portal-section--soft">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('应用场景', 'Applications') }}</span>
          <h2>{{ text('面向真实运行环境设计', 'Designed for real operating environments') }}</h2>
          <p>{{ text('根据通行效率、检查精度、空间条件和管理流程，提供针对性的系统配置与交付方案。', 'System configuration and delivery are shaped by throughput, detection goals, site constraints and procedures.') }}</p>
        </header>
        <div class="portal-scenario-grid">
          <article
            v-for="(item, index) in applications"
            :key="localized(item.title)"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span><h3>{{ localized(item.title) }}</h3><p>{{ localized(item.copy) }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="portal-section portal-company-band">
      <div class="container portal-company-band__grid">
        <div class="portal-company-band__media">
          <img
            :src="COMPANY_IMAGE"
            :alt="text('杭州技卫士总部办公楼', 'JWS Hangzhou headquarters building')"
          >
        </div>
        <div class="portal-company-band__content">
          <span class="portal-eyebrow">{{ text('关于技卫士', 'About JWS') }}</span>
          <h2>{{ text('以持续研发支撑可靠交付', 'Continuous engineering for dependable delivery') }}</h2>
          <p>{{ text('杭州技卫士是一家聚焦人体安检成像技术与智能安检系统的科技企业。公司围绕扫描成像、图像处理、智能识别和业务平台持续投入研发，并将技术能力落实到设备制造、项目部署和售后服务全过程。', 'JWS develops body screening imaging and intelligent security systems, carrying engineering capability through manufacturing, deployment and service.') }}</p>
          <dl>
            <div><dt>{{ text('杭州', 'Hangzhou') }}</dt><dd>{{ text('公司总部', 'Headquarters') }}</dd></div>
            <div><dt>{{ text('全流程', 'End to end') }}</dt><dd>{{ text('方案、交付、培训、服务', 'Planning, delivery, training and service') }}</dd></div>
          </dl>
          <RouterLink
            class="text-link"
            to="/company/company"
          >
            {{ text('了解公司与研发能力', 'Company and engineering capability') }} <b>→</b>
          </RouterLink>
        </div>
      </div>
    </section>

    <section
      v-if="activeVideo"
      class="portal-section portal-video-evidence"
    >
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('视频中心', 'Video evidence') }}</span>
          <h2>{{ text('产品演示视频', 'See how the equipment works in practice') }}</h2>
          <p>{{ text('真实设备、操作流程和成像界面，直观呈现人体安检设备的运行方式。', 'Real equipment, operating workflows and imaging interfaces show the screening process directly.') }}</p>
        </header>
        <div class="portal-video-layout">
          <article class="portal-video-stage">
            <div class="portal-video-player">
              <video
                :key="activeVideo.id"
                ref="videoElement"
                controls
                playsinline
                preload="metadata"
                :src="activeVideo.playbackUrl || undefined"
                :poster="activeVideo.posterUrl || undefined"
                @ended="videoStarted = false"
              >{{ text('当前浏览器不支持视频播放。', 'Your browser does not support video playback.') }}</video>
              <button
                v-if="!videoStarted"
                class="portal-video-cover"
                type="button"
                :aria-label="text(`播放${activeVideo.title}`, `Play ${activeVideo.title}`)"
                @click="playActiveVideo"
              >
                <img
                  v-if="activeVideo.posterUrl"
                  :src="activeVideo.posterUrl"
                  alt=""
                >
                <span><i aria-hidden="true" /><strong>{{ text('播放演示', 'Play video') }}</strong></span>
              </button>
              <small v-if="formatDuration(activeVideo.durationSec)">{{ formatDuration(activeVideo.durationSec) }}</small>
            </div>
            <div class="portal-video-stage__copy">
              <span>{{ text('产品演示', 'Product demonstration') }}</span><h3>{{ activeVideo.title }}</h3><p>{{ activeVideo.description }}</p>
            </div>
          </article>
          <aside
            class="portal-video-playlist"
            :aria-label="text('演示影像', 'Video playlist')"
          >
            <button
              v-for="(item, index) in videos"
              :key="item.id"
              type="button"
              :class="{ 'is-active': index === activeVideoIndex }"
              :aria-pressed="index === activeVideoIndex"
              @click="selectVideo(index)"
            >
              <img
                v-if="item.posterUrl"
                :src="item.posterUrl"
                alt=""
                loading="lazy"
              >
              <span><small>{{ String(index + 1).padStart(2, '0') }}<template v-if="formatDuration(item.durationSec)"> · {{ formatDuration(item.durationSec) }}</template></small><strong>{{ item.title }}</strong></span>
            </button>
          </aside>
        </div>
      </div>
    </section>

    <section
      v-if="activeCase"
      class="portal-section portal-section--soft portal-project-showcase"
    >
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('行业案例', 'Projects') }}</span>
          <h2>{{ text('项目现场是产品能力的直接证明', 'Field deployment is the clearest proof') }}</h2>
          <p>{{ text('从司法监所到机场与矿山，持续积累不同安检场景下的部署与运行经验。', 'Deployment experience across corrections, airports, mining and other demanding environments.') }}</p>
        </header>
        <div class="portal-case-carousel">
          <RouterLink
            class="portal-case-carousel__stage"
            :to="`/cases/${activeCase.slug}`"
          >
            <img
              v-if="activeCase.coverUrl"
              :src="activeCase.coverUrl"
              :alt="activeCase.title"
            >
            <div
              v-else
              class="media-placeholder"
            >
              JWS
            </div>
            <div class="portal-case-carousel__copy">
              <span>{{ caseCategory(activeCase) }}</span><h3>{{ activeCase.title }}</h3><p>{{ activeCase.excerpt }}</p><small>{{ text('查看项目案例', 'View project') }} →</small>
            </div>
          </RouterLink>
          <div
            v-if="cases.length > 1"
            class="portal-case-carousel__controls"
          >
            <button
              type="button"
              :aria-label="text('上一个行业案例', 'Previous project')"
              @click="showCase(-1)"
            >
              ←
            </button>
            <button
              type="button"
              :aria-label="text('下一个行业案例', 'Next project')"
              @click="showCase(1)"
            >
              →
            </button>
          </div>
          <div class="portal-case-carousel__footer">
            <span>{{ String(activeCaseIndex + 1).padStart(2, '0') }} / {{ String(cases.length).padStart(2, '0') }}</span><RouterLink
              class="text-link"
              to="/cases"
            >
              {{ text('查看全部行业案例', 'View all projects') }} <b>→</b>
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

    <section class="portal-section portal-delivery">
      <div class="container">
        <header class="portal-section-heading portal-section-heading--delivery">
          <span>{{ text('项目服务', 'Project services') }}</span>
          <h2>{{ text('把复杂的安检需求落实为可运行的系统', 'Turn complex requirements into an operational system') }}</h2>
          <p>{{ text('从需求澄清到持续服务，建立清晰、可追踪的项目协作流程。', 'A clear, traceable process from requirement definition through ongoing service.') }}</p>
        </header>
        <ol class="portal-delivery__steps">
          <li
            v-for="(item, index) in deliverySteps"
            :key="localized(item.title)"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span><strong>{{ localized(item.title) }}</strong><p>{{ localized(item.copy) }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="portal-service-contact">
      <div class="container portal-service-contact__layout">
        <div>
          <span class="portal-eyebrow">{{ text('营销与技术支持', 'Sales and technical support') }}</span><h2>{{ text('我们提供完整的项目服务', 'Support across the project lifecycle') }}</h2><p>{{ text('从设备选型、现场评估到交付与持续支持，营销中心为项目提供清晰、及时的协同服务。', 'From product selection and site assessment to delivery and support, our team keeps project collaboration clear and responsive.') }}</p><RouterLink
            class="text-link"
            to="/contact"
          >
            {{ text('提交项目需求', 'Submit project requirements') }} <b>→</b>
          </RouterLink>
        </div>
        <dl v-if="primaryContactChannels.length">
          <div v-for="channel in primaryContactChannels" :key="`${channel.type}-${channel.label}-${channel.value}`"><dt>{{ channel.label }}</dt><dd><a v-if="contactHref(channel)" :href="contactHref(channel) || undefined" :target="isExternalContactHref(contactHref(channel)) ? '_blank' : undefined" :rel="isExternalContactHref(contactHref(channel)) ? 'noreferrer' : undefined">{{ channel.value }}</a><span v-else>{{ channel.value }}</span></dd></div>
        </dl>
      </div>
    </section>
  </div>
</template>
